import { CONFIG } from "@shared/config";
import { Products, Addons } from "@shared/types/config";
import { RouteName } from "@shared/types/routes";
import type { NavigationItem } from "@shared/store/ui/types";

const {
  application: {
    products,
    addons,
    initialization: { landingPage },
  },
} = CONFIG;

// START -- KEEP THESE IN SYNC --
export const allowedRoutesByProduct: Record<
  Products | Addons.SEARCH,
  RouteName[]
> = {
  [Addons.SEARCH]: [RouteName.SEARCH, RouteName.SEARCH_DETAILS],
  [Products.STATIONS]: [
    RouteName.MORE_LEGEND,
    RouteName.STATION_ROOT,
    RouteName.STATION,
    RouteName.STATION_SEND_TO_PHONE,
    RouteName.STATION_PRICING,
    RouteName.STATION_CHARGE_CARDS,
    RouteName.STATION_CHARGE_CARDS_PRICING,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_ROOT,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_PRICING,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
  ],
  [Products.ROUTING]: [
    RouteName.PLAN_ROUTE,
    RouteName.PLAN_TRUCK_ROUTE,
    RouteName.ROUTE_OVERVIEW_ROOT,
    RouteName.ROUTE_OVERVIEW_DETAILS,
    RouteName.ROUTE_OVERVIEW_DETAILS_SEND_TO_PHONE,
    RouteName.ROUTE_OVERVIEW_DETAILS_AMENITY_CHARGE_STOP,
    RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED,
    RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED_LOCATION,
    RouteName.ROUTE_OVERVIEW_STATION_ROOT,
    RouteName.ROUTE_OVERVIEW_STATION,
    RouteName.ROUTE_OVERVIEW_STATION_PRICING,
    RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS,
    RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
  ],
  [Products.RANGE]: [RouteName.RANGE_SPIDER, RouteName.RANGE_SPIDER_OVERVIEW],
};

const navigationRoutes: Record<NavigationItem, RouteName[]> = {
  search: [
    RouteName.SEARCH,
    RouteName.SEARCH_DETAILS,
    RouteName.STATION_ROOT,
    RouteName.STATION_CHARGE_CARDS,
  ],
  routing: [
    RouteName.PLAN_ROUTE,
    RouteName.PLAN_TRUCK_ROUTE,
    RouteName.ROUTE_OVERVIEW_ROOT,
    RouteName.ROUTE_OVERVIEW_DETAILS,
    RouteName.ROUTE_OVERVIEW_DETAILS_SEND_TO_PHONE,
    RouteName.ROUTE_OVERVIEW_DETAILS_AMENITY_CHARGE_STOP,
    RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED,
    RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED_LOCATION,
    RouteName.ROUTE_OVERVIEW_STATION_ROOT,
    RouteName.ROUTE_OVERVIEW_STATION,
    RouteName.ROUTE_OVERVIEW_STATION_PRICING,
    RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS,
    RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
    RouteName.STATION_ROOT,
    RouteName.STATION,
    RouteName.STATION_SEND_TO_PHONE,
    RouteName.STATION_PRICING,
    RouteName.STATION_CHARGE_CARDS,
  ],
  range: [
    RouteName.RANGE_SPIDER,
    RouteName.RANGE_SPIDER_OVERVIEW,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_ROOT,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_PRICING,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS,
    RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
    RouteName.STATION_ROOT,
    RouteName.STATION,
    RouteName.STATION_SEND_TO_PHONE,
    RouteName.STATION_PRICING,
    RouteName.STATION_CHARGE_CARDS,
  ],
};
// END -- KEEP THESE IN SYNC --

export const getAllowedNavigationRoutes = (
  navigation: NavigationItem[],
): Set<RouteName> => {
  const routes: RouteName[] = Object.entries(navigationRoutes)
    .filter(([navigationItem]) => {
      return navigation.includes(navigationItem as NavigationItem);
    })
    .flatMap(([, routes]) => routes);

  return new Set([...routes]);
};

export const allowedRoutes: RouteName[] = Object.entries(allowedRoutesByProduct)
  .filter(([product]) => {
    const isProductEnabled = products.includes(product as Products);
    const isAddonEnabled = addons.includes(product as Addons);
    return isProductEnabled || isAddonEnabled;
  })
  .flatMap(([, routes]) => routes);

export const getLandingPage = (): RouteName => {
  if (!landingPage || !allowedRoutes.includes(landingPage)) {
    return allowedRoutes[0];
  }
  return landingPage;
};
